import * as React from "react"
import { X } from "lucide-react"
import { Drawer as DrawerPrimitive } from "vaul"

import { useDrawerKeyboardInset } from "@/hooks/useDrawerKeyboardInset"
import { cn } from "@/lib/utils"
import { playHaptic } from "@/shared/utils/haptics"
import ReportOverlayToApp from "./ReportOverlayToApp"

// `repositionInputs` is off: vaul's keyboard handling lifts the drawer twice on iOS
// and pushes it off the top of the screen. `DrawerContent` keeps it above the
// keyboard instead (see `useDrawerKeyboardInset`).
const Drawer = ({
  shouldScaleBackground = false,
  repositionInputs = false,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    repositionInputs={repositionInputs}
    onOpenChange={(open) => {
      // Cerrar cualquier drawer dispara un toque ligero (swipe, overlay, Esc).
      // Centralizado aquí para que todo drawer lo tenga; no-op fuera de la app.
      if (!open) playHaptic("tap")
      onOpenChange?.(open)
    }}
    {...props}
  />
)
Drawer.displayName = "Drawer"

const DrawerTrigger = DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal

const DrawerClose = DrawerPrimitive.Close

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-[100] bg-h-primary-20/40", className)}
    {...props}
  />
))
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

// Set `hideCloseButton` when the drawer's body already renders its own close
// affordance (e.g. a header row that pairs an X with a back button) so the
// universal control doesn't collide with a layout-specific one.
type DrawerContentProps = React.ComponentPropsWithoutRef<
  typeof DrawerPrimitive.Content
> & {
  hideCloseButton?: boolean
  /** Clases para el scrim. Lo necesita el modal anidado: al taparse el de
   * abajo hay que apagar TAMBIÉN su scrim, o dos se suman y la pantalla queda
   * casi negra. */
  overlayClassName?: string
  hideHandle?: boolean
}

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  DrawerContentProps
>(({ className, children, hideCloseButton = false, hideHandle = false, overlayClassName, ...props }, ref) => {
  // The mounted content element, for the keyboard inset (it mounts when the drawer opens).
  const [contentNode, setContentNode] = React.useState<HTMLDivElement | null>(null)
  useDrawerKeyboardInset(contentNode)
  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      setContentNode(node)
      if (typeof ref === "function") ref(node)
      else if (ref) ref.current = node
    },
    [ref]
  )

  return (
    <DrawerPortal>
      <DrawerOverlay className={overlayClassName} />
      <DrawerPrimitive.Content
        ref={setRefs}
        className={cn(
          "fixed inset-x-0 bottom-0 z-[100] mt-24 flex h-auto flex-col rounded-t-3xl border bg-background",
          className
        )}
        {...props}
      >
        <ReportOverlayToApp />
        {!hideHandle && <div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />}
        {!hideCloseButton && (
          <DrawerPrimitive.Close
            aria-label="Cerrar"
            style={{ boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px" }}
            className="absolute top-3 right-3 z-30 cursor-pointer rounded-full bg-white p-2 text-black"
          >
            <X className="h-[18px] w-[18px]" strokeWidth={2.5} />
          </DrawerPrimitive.Close>
        )}
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  )
})
DrawerContent.displayName = "DrawerContent"

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)}
    {...props}
  />
)
DrawerHeader.displayName = "DrawerHeader"

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
)
DrawerFooter.displayName = "DrawerFooter"

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DrawerTitle.displayName = DrawerPrimitive.Title.displayName

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DrawerDescription.displayName = DrawerPrimitive.Description.displayName

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
}
